import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, X, Instagram, Globe, Facebook, Twitter, Star, Clock, Tag, Wallet, Mail, Bookmark } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useWedding } from '../../context/WeddingContext'
import VerifiedBadge from '../../components/aisle/VerifiedBadge'
import AvailabilityCalendar from '../../components/aisle/AvailabilityCalendar'

const CATEGORY_LABELS = {
  photographer:'Photographer', videographer:'Videographer', decorator:'Decorator',
  mc_dj:'MC / DJ', caterer:'Caterer', aso_ebi:'Aso Ebi', makeup:'Makeup Artist',
  bridal_fashion:'Bridal Fashion', venue:'Venue', live_band:'Live Band',
  cake:'Cake Designer', planner:'Event Planner',
}
const fmtPrice = (n, cur='NGN') => n ? `${cur==='NGN'?'₦':cur+' '}${Number(n).toLocaleString()}` : '—'

function responseText(mins) {
  if (!mins) return 'Usually responds quickly'
  if (mins < 60) return `Usually responds within ${mins} minutes`
  const h = Math.round(mins / 60)
  return `Usually responds within ${h} hour${h === 1 ? '' : 's'}`
}

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, twitter: Twitter, website: Globe }
function socialUrl(key, val) {
  const v = String(val).replace(/^@/, '')
  if (key === 'instagram') return `https://instagram.com/${v}`
  if (key === 'facebook')  return `https://facebook.com/${v}`
  if (key === 'twitter')   return `https://twitter.com/${v}`
  return v.startsWith('http') ? v : `https://${v}`
}

const arr = x => Array.isArray(x) ? x : []

const Pill = ({ children, dark }) => (
  <span style={{
    background: dark ? 'rgba(255,255,255,0.18)' : '#F3F0EA',
    backdropFilter: dark ? 'blur(4px)' : undefined,
    border: dark ? '1px solid rgba(255,255,255,0.3)' : '1px solid #E5E0D8',
    borderRadius:100, padding:'0.2rem 0.7rem', fontFamily:'Nunito Sans, sans-serif',
    fontSize:'0.7rem', fontWeight:600, color: dark ? '#fff' : '#6B6B6B', whiteSpace:'nowrap',
  }}>{children}</span>
)

export default function VendorProfilePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { wedding } = useWedding()
  const [vendor, setVendor] = useState(undefined) // undefined=loading, null=not found
  const [lightbox, setLightbox] = useState(null)   // gallery index or null
  const [shortlisted, setShortlisted] = useState(false)
  const [savingShortlist, setSavingShortlist] = useState(false)
  const [busy, setBusy] = useState(new Set())

  useEffect(() => {
    const loadBusy = () => supabase.from('vendor_availability').select('day').eq('vendor_id', id)
      .then(({ data }) => setBusy(new Set((data || []).map(r => r.day))), () => {})
    loadBusy()
    const ch = supabase.channel(`vendor-avail-${id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'vendor_availability', filter:`vendor_id=eq.${id}` }, loadBusy)
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [id])

  useEffect(() => {
    let live = true
    const loadVendor = () => supabase.rpc('aisle_get_vendor', { p_id: id }).then(({ data }) => {
      if (live) setVendor((data && data[0]) || null)
    }, () => { if (live) setVendor(null) })
    loadVendor()
    // Live: the vendor editing their listing refetches it here (Broadcast).
    const ch = supabase.channel(`aisle-vendor-${id}`)
      .on('broadcast', { event: 'updated' }, () => loadVendor())
      .subscribe()
    return () => { live = false; supabase.removeChannel(ch) }
  }, [id])

  useEffect(() => {
    if (!wedding?.id || !id) return
    supabase.from('vendor_shortlist').select('id').eq('wedding_id', wedding.id).eq('vendor_id', id).maybeSingle()
      .then(({ data }) => setShortlisted(!!data), () => {})
  }, [wedding?.id, id])

  async function toggleShortlist() {
    if (!wedding?.id || savingShortlist) return
    setSavingShortlist(true)
    const next = !shortlisted
    setShortlisted(next) // optimistic
    try {
      if (next) await supabase.from('vendor_shortlist').insert({ wedding_id: wedding.id, vendor_id: id })
      else      await supabase.from('vendor_shortlist').delete().eq('wedding_id', wedding.id).eq('vendor_id', id)
    } catch { setShortlisted(!next) }
    setSavingShortlist(false)
  }

  if (vendor === undefined) return <p style={{ textAlign:'center', color:'#BCBCBC', padding:'4rem', fontFamily:'Nunito Sans, sans-serif' }}>Loading…</p>
  if (vendor === null) return (
    <div style={{ textAlign:'center', padding:'4rem 1rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#9B9B9B', marginBottom:'1rem' }}>Vendor not found.</p>
      <button onClick={() => navigate('/dashboard/aisle')} className="btn-secondary">Back to The Aisle</button>
    </div>
  )

  const weddingDate = wedding?.wedding_date ? String(wedding.wedding_date).slice(0, 10) : null
  const prettyDate = weddingDate ? new Date(weddingDate).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : ''
  const gallery = arr(vendor.gallery_images)
  const cover = gallery[0] || vendor.profile_image_url
  const socials = Object.entries(vendor.social_links || {})

  return (
    <div style={{ paddingBottom:'5rem' }}>
      {/* Back → this vendor's category (deterministic, not browser history) */}
      <button onClick={() => navigate(`/dashboard/aisle/c/${vendor.category}`)}
        style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none', cursor:'pointer',
          fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', marginBottom:'1rem', padding:0 }}>
        <ChevronLeft size={16} /> {CATEGORY_LABELS[vendor.category] || 'The Aisle'}
      </button>

      {/* Cover */}
      <div style={{ position:'relative', borderRadius:'1.25rem', overflow:'hidden', aspectRatio:'16/9',
        background:'#E5E0D8', boxShadow:'0 10px 34px rgba(0,0,0,0.12)' }}>
        {cover && <img src={cover} alt={vendor.business_name} style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }} />}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(26,26,26,0.8) 0%, rgba(26,26,26,0.15) 50%, rgba(26,26,26,0.05) 100%)' }} />
        <div style={{ position:'absolute', top:14, left:14 }}>
          <VerifiedBadge status={vendor.verification_status || 'verified'} size={20} />
        </div>
        <div style={{ position:'absolute', left:0, right:0, bottom:0, padding:'1.25rem 1.5rem' }}>
          <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'2rem', color:'#fff',
            margin:'0 0 0.6rem', lineHeight:1.15, textShadow:'0 2px 10px rgba(0,0,0,0.4)' }}>{vendor.business_name}</h1>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
            <Pill dark>{CATEGORY_LABELS[vendor.category] || vendor.category}</Pill>
            {vendor.city && <Pill dark>{vendor.city}</Pill>}
            <Pill dark>from {fmtPrice(vendor.starting_price, vendor.price_currency)}</Pill>
          </div>
        </div>
      </div>

      {/* Vendor Vow */}
      {vendor.vendor_vow && (
        <div style={{ textAlign:'center', maxWidth:620, margin:'2.25rem auto 0', padding:'0 1rem' }}>
          <div style={{ fontFamily:'Gibs, serif', fontSize:'3rem', color:'#C4826A', lineHeight:0.5, height:'1.5rem' }}>“</div>
          <p style={{ fontFamily:'Gibs, serif', fontStyle:'italic', fontSize:'1.1rem', color:'#1A1A1A', lineHeight:1.8, margin:'0 0 0.75rem' }}>
            {vendor.vendor_vow}
          </p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B' }}>— {vendor.business_name}</p>
        </div>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <div style={{ marginTop:'2.25rem' }}>
          <div style={{ display:'flex', gap:'0.75rem', overflowX:'auto', paddingBottom:'0.5rem', scrollbarWidth:'thin' }}>
            {gallery.map((src, i) => (
              <img key={i} src={src} alt="" onClick={() => setLightbox(i)} loading="lazy"
                style={{ width:240, height:300, flexShrink:0, objectFit:'cover', borderRadius:'0.875rem', cursor:'pointer', background:'#E5E0D8' }} />
            ))}
          </div>
        </div>
      )}

      {/* Details + Quick Facts */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'1.5rem', marginTop:'2.25rem', alignItems:'flex-start' }}>
        {/* Left: description, languages, socials */}
        <div style={{ flex:'1 1 320px', minWidth:0 }}>
          {vendor.description && (
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.92rem', color:'#3A3A3A', lineHeight:1.8, margin:'0 0 1.25rem' }}>
              {vendor.description}
            </p>
          )}
          {arr(vendor.languages).length > 0 && (
            <div style={{ marginBottom:'1.25rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#9B9B9B', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 0.5rem' }}>Languages</p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.4rem' }}>
                {arr(vendor.languages).map(l => <Pill key={l}>{l}</Pill>)}
              </div>
            </div>
          )}
          {socials.length > 0 && (
            <div style={{ display:'flex', gap:'0.5rem' }}>
              {socials.map(([key, val]) => {
                const Icon = SOCIAL_ICONS[key] || Globe
                return (
                  <a key={key} href={socialUrl(key, val)} target="_blank" rel="noreferrer"
                    style={{ width:38, height:38, borderRadius:'50%', border:'1px solid #E5E0D8', display:'flex',
                      alignItems:'center', justifyContent:'center', color:'#C4826A', background:'#fff' }}>
                    <Icon size={17} />
                  </a>
                )
              })}
            </div>
          )}
        </div>

        {/* Right: Quick Facts */}
        <div style={{ flex:'1 1 260px', maxWidth:320 }}>
          <div className="card" style={{ padding:'1.25rem' }}>
            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', margin:'0 0 0.875rem' }}>Quick Facts</h3>
            <Fact Icon={Wallet} label="Starting price" value={fmtPrice(vendor.starting_price, vendor.price_currency)} />
            <Fact Icon={Clock} label="Response time" value={responseText(vendor.response_time_mins)} />
            {arr(vendor.specialties).length > 0 && (
              <Fact Icon={Tag} label="Specialties" value={arr(vendor.specialties).join(', ')} />
            )}
            {vendor.rating_count > 0 && (
              <Fact Icon={Star} label="Rating" value={`${Number(vendor.rating_avg).toFixed(1)} · ${vendor.rating_count} reviews`} />
            )}
          </div>
        </div>
      </div>

      {/* Availability (read-only for the couple) */}
      <div style={{ marginTop:'2.25rem', maxWidth:420 }}>
        <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 0.75rem' }}>Availability</h3>
        <AvailabilityCalendar blocked={busy} highlight={weddingDate} />
        {weddingDate && (
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', margin:'0.75rem 0 0', fontWeight:700,
            color: busy.has(weddingDate) ? '#B91C1C' : '#3D8B5E' }}>
            {busy.has(weddingDate) ? `Unavailable on your date (${prettyDate})` : `Available on your date (${prettyDate}) 🎉`}
          </p>
        )}
      </div>

      {/* Action bar (sticky bottom) */}
      <div style={{ position:'sticky', bottom:0, marginTop:'2rem', padding:'0.875rem 0',
        background:'linear-gradient(to top, #FAFAF8 70%, rgba(250,250,248,0))', display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
        <button onClick={() => navigate(`/dashboard/aisle/inquiry/${id}`)}
          style={{ flex:'1 1 200px', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
            background:'#C4826A', color:'#fff', border:'none', borderRadius:100, padding:'0.875rem',
            fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
          <Mail size={16} /> Send Inquiry
        </button>
        <button onClick={toggleShortlist} disabled={savingShortlist}
          style={{ flex:'1 1 160px', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
            background:'#fff', color:shortlisted ? '#9BAF9B' : '#C4826A',
            border:`1.5px solid ${shortlisted ? '#9BAF9B' : '#E5E0D8'}`, borderRadius:100, padding:'0.875rem',
            fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', cursor:'pointer' }}>
          <Bookmark size={16} fill={shortlisted ? '#9BAF9B' : 'none'} />
          {shortlisted ? 'Saved to Shortlist' : 'Save to Shortlist'}
        </button>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div onClick={() => setLightbox(null)} style={{ position:'fixed', inset:0, zIndex:1000,
          background:'rgba(0,0,0,0.9)', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem' }}>
          <button onClick={() => setLightbox(null)} style={{ position:'absolute', top:18, right:18, background:'none', border:'none', color:'#fff', cursor:'pointer' }}><X size={28} /></button>
          {gallery.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox - 1 + gallery.length) % gallery.length) }}
              style={{ position:'absolute', left:14, background:'none', border:'none', color:'#fff', cursor:'pointer' }}><ChevronLeft size={34} /></button>
          )}
          <img src={gallery[lightbox]} alt="" onClick={e => e.stopPropagation()}
            style={{ maxWidth:'90%', maxHeight:'85vh', objectFit:'contain', borderRadius:'0.5rem' }} />
          {gallery.length > 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox((lightbox + 1) % gallery.length) }}
              style={{ position:'absolute', right:14, background:'none', border:'none', color:'#fff', cursor:'pointer' }}><ChevronRight size={34} /></button>
          )}
        </div>
      )}
    </div>
  )
}

function Fact({ Icon, label, value }) {
  return (
    <div style={{ display:'flex', gap:'0.6rem', alignItems:'flex-start', padding:'0.5rem 0', borderTop:'1px solid #F3F0EA' }}>
      <Icon size={15} color="#C4826A" style={{ marginTop:2, flexShrink:0 }} />
      <div style={{ minWidth:0 }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.66rem', color:'#9B9B9B', margin:0, fontWeight:600 }}>{label}</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', margin:0 }}>{value}</p>
      </div>
    </div>
  )
}
